import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationPrompt from '../components/notifications/NotificationPrompt';
import { bookingApi } from '../utils/bookingApi';
import { readDashboardCache, writeDashboardCache } from '../utils/dashboardCache';
import StaffDocumentsCard from '../components/StaffDocumentsCard';
import MySessionsCard from '../components/sessions/MySessionsCard';
import WelfareOutcomesCard from '../components/welfare/WelfareOutcomesCard';
import DashboardPanel from '../components/DashboardPanel';
import ChildSignInCard from '../components/ChildSignInCard';
import {
  CalendarDaysIcon,
  ShoppingBagIcon,
  TrophyIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { homeTilesFor, tilesFor, staffDocumentsOn } from '../data/staffTiles';
import { STAFF_UPDATES } from '../data/staffUpdates';
import HomeTilePicker from '../components/HomeTilePicker';
import CodeOfDayCard from '../components/CodeOfDayCard';
import PaymentsBehindCard from '../components/PaymentsBehindCard';
import GymnastLimitBanner from '../components/GymnastLimitBanner';
import MembershipPaymentWarning from '../components/payments/MembershipPaymentWarning';
import Toast from '../components/Toast';
import useToast from '../hooks/useToast';
import { useFeatures } from '../hooks/useFeature';
import { typeName } from '../utils/sessionTypes';

const Dashboard = () => {
  const {
    user, isClubAdmin, isStaff, can,
    updateUser,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast, showToast, dismissToast } = useToast();

  // Coming back from Stripe after setting up paying Run The Club
  useEffect(() => {
    const billing = new URLSearchParams(location.search).get('billing');
    if (billing !== 'done' && billing !== 'cancelled') return;
    if (billing === 'done') showToast('Thanks, paying Run The Club is set up. It can take a minute to show here.');
    else showToast('Setting up paying Run The Club was cancelled.', 'warning');
    navigate('/dashboard', { replace: true });
  }, [location.search, navigate, showToast]);

  // Staff get the staff home page; each panel on it follows their access
  const isAdminOrCoach = isStaff;
  const seesSessions = isStaff && can('sessions');
  const seesCompetitions = isStaff && can('competitions');
  const [pickingTiles, setPickingTiles] = useState(false);

  // ── Noticeboard panel ──────────────────────────────────────────────────────
  // Seeded from the per-user cache (see utils/dashboardCache) so the panel at
  // the top of the page doesn't pop in and push everything below it down.
  const [cachedNoticeboard] = useState(() => readDashboardCache(user?.id).noticeboard);
  const [noticeboardPosts, setNoticeboardPosts] = useState(cachedNoticeboard ?? []);
  const [noticeboardLoading, setNoticeboardLoading] = useState(!cachedNoticeboard);

  useEffect(() => {
    bookingApi.getNoticeboard()
      .then(r => {
        setNoticeboardPosts(r.data);
        writeDashboardCache(user?.id, { noticeboard: r.data });
      })
      .catch(() => {})
      .finally(() => setNoticeboardLoading(false));
  }, [user?.id]);

  const [expandedPostId, setExpandedPostId] = useState(null);

  const unreadCount = noticeboardPosts.filter(p => !p.isRead).length;

  const sortedPosts = [...noticeboardPosts].sort((a, b) => {
    if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
    const priorityOrder = { URGENT: 0, IMPORTANT: 1, INFO: 2 };
    if (a.priority !== b.priority) return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
    return 0;
  });

  const handleExpandPost = async (post) => {
    setExpandedPostId(prev => prev === post.id ? null : post.id);
    if (!post.isRead) {
      try {
        await bookingApi.markNoticeboardRead(post.id);
        setNoticeboardPosts(prev => {
          const next = prev.map(p => p.id === post.id ? { ...p, isRead: true } : p);
          writeDashboardCache(user?.id, { noticeboard: next });
          return next;
        });
      } catch { /* ignore */ }
    }
  };

  function stripHtml(html) {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  // ── Today widget (admin/coach only) ────────────────────────────────────────
  // Seed from the per-coach cache so a return visit renders these straight
  // away, instead of popping them in above the tiles; the effect below always
  // refreshes them in the background.
  const [cached] = useState(() => (seesSessions ? readDashboardCache(user?.id) : {}));
  const [todaySessions, setTodaySessions] = useState(cached.todaySessions ?? []);
  const [todayLoading, setTodayLoading] = useState(seesSessions && !cached.todaySessions);

  useEffect(() => {
    if (!seesSessions) return;
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    bookingApi.getSessions(now.getFullYear(), now.getMonth() + 1)
      .then(res => {
        const todays = res.data.filter(s => new Date(s.date).toISOString().split('T')[0] === todayStr);
        setTodaySessions(todays);
        writeDashboardCache(user?.id, { todaySessions: todays });
      })
      .catch(() => {})
      .finally(() => setTodayLoading(false));
  }, [seesSessions, user?.id]);

  // Sessions starting within 15 minutes or running now, worked out on each
  // render so cached sessions still highlight correctly later in the day.
  const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
  const activeSessions = todaySessions.filter(s => {
    const [sh, sm] = s.startTime.split(':').map(Number);
    const [eh, em] = s.endTime.split(':').map(Number);
    return nowMins >= sh * 60 + sm - 15 && nowMins <= eh * 60 + em;
  });

  const showTodayWidget = seesSessions && todaySessions.length > 0;

  // ── Competition panel ──────────────────────────────────────────────────────
  // Shown from the last visit straight away (so the panel doesn't pop in),
  // then refreshed.
  const has = useFeatures();
  const compsOn = has('competitions');
  const [cachedComps] = useState(() => readDashboardCache(user?.id).competitions || {});
  const [myCompEntries, setMyCompEntries] = useState(cachedComps.entries ?? []);
  const [compSummary, setCompSummary] = useState(cachedComps.summary ?? null); // admin only
  const [upcomingEvents, setUpcomingEvents] = useState(() => (cachedComps.events ?? []).filter(e => new Date(e.startDate) >= new Date())); // admin only

  useEffect(() => {
    if (!isAdminOrCoach && compsOn) {
      bookingApi.getMyCompetitionEntries()
        .then(r => {
          setMyCompEntries(r.data);
          writeDashboardCache(user?.id, { competitions: { entries: r.data } });
        })
        .catch(() => {});
    }
  }, [isAdminOrCoach, compsOn, user?.id]);

  useEffect(() => {
    if (seesCompetitions && compsOn) {
      Promise.all([
        bookingApi.getCompetitionEntrySummary().then(r => r.data).catch(() => null),
        bookingApi.getCompetitionEvents().then(r => r.data.filter(e => new Date(e.startDate) >= new Date())).catch(() => null),
      ]).then(([summary, events]) => {
        if (summary) setCompSummary(summary);
        if (events) setUpcomingEvents(events);
        if (summary && events) writeDashboardCache(user?.id, { competitions: { summary, events } });
      });
    }
  }, [seesCompetitions, compsOn, user?.id]);

  // ── Noticeboard panel ──────────────────────────────────────────────────────
  const PRIORITY_CONFIG = {
    URGENT:    { label: 'Urgent',    className: 'dashboard-nb-item--urgent' },
    IMPORTANT: { label: 'Important', className: 'dashboard-nb-item--important' },
    INFO:      { label: null,        className: '' },
  };

  const renderNoticeboard = (collapsible) => (
    <DashboardPanel
      className="dashboard-nb"
      headerClassName="dashboard-nb__header"
      title={<span className="dashboard-nb__title">📢 Noticeboard</span>}
      badge={unreadCount > 0 ? (
        <span className="dashboard-nb__badge">{unreadCount} unread</span>
      ) : null}
      collapsible={collapsible}
      defaultOpen={!collapsible}
    >
      {noticeboardLoading ? (
        <p className="dashboard-nb__empty">Loading…</p>
      ) : sortedPosts.length === 0 ? (
        <p className="dashboard-nb__empty">No notices at the moment.</p>
      ) : (
        <ul className="dashboard-nb__list">
          {sortedPosts.slice(0, 3).map(post => {
            const cfg = PRIORITY_CONFIG[post.priority] ?? PRIORITY_CONFIG.INFO;
            const isExpanded = expandedPostId === post.id;
            const snippet = stripHtml(post.body);
            return (
              <li
                key={post.id}
                className={`dashboard-nb__item${!post.isRead ? ' dashboard-nb__item--unread' : ''} ${cfg.className}`}
                onClick={() => handleExpandPost(post)}
              >
                <div className="dashboard-nb__item-top">
                  <div className="dashboard-nb__item-title-row">
                    {cfg.label && (
                      <span className={`dashboard-nb__priority-badge dashboard-nb__priority-badge--${post.priority.toLowerCase()}`}>
                        {cfg.label}
                      </span>
                    )}
                    <span className="dashboard-nb__item-title">{post.title}</span>
                  </div>
                  <span className="dashboard-nb__item-time">{timeAgo(post.createdAt)}</span>
                </div>
                {!isExpanded && snippet && (
                  <p className="dashboard-nb__item-snippet">
                    {snippet.length > 120 ? snippet.slice(0, 120) + '…' : snippet}
                  </p>
                )}
                {isExpanded && (
                  <div
                    className="dashboard-nb__item-body"
                    dangerouslySetInnerHTML={{ __html: post.body }}
                    onClick={e => e.stopPropagation()}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="dashboard-nb__footer">
        <Link to="/booking/noticeboard" className="dashboard-nb__cta">View all notices</Link>
      </div>
    </DashboardPanel>
  );

  // ── Section tiles ──────────────────────────────────────────────────────────
  const memberTiles = (
    <div className="dashboard-tiles">
      <Link to="/booking" className="dashboard-tile dashboard-tile--bookings" state={{ skipAdminRedirect: true }}>
        <CalendarDaysIcon className="dashboard-tile__icon" />
        <span className="dashboard-tile__label">Bookings</span>
      </Link>
      {has('shop') && (
        <Link to="/booking/shop" className="dashboard-tile dashboard-tile--shop">
          <ShoppingBagIcon className="dashboard-tile__icon" />
          <span className="dashboard-tile__label">Shop</span>
        </Link>
      )}
      <Link to="/my-progress" className="dashboard-tile dashboard-tile--tracking">
        <TrophyIcon className="dashboard-tile__icon" />
        <span className="dashboard-tile__label">Skill Tracking</span>
      </Link>
      <Link to="/booking/my-account" className="dashboard-tile dashboard-tile--account">
        <UserIcon className="dashboard-tile__icon" />
        <span className="dashboard-tile__label">My Account</span>
      </Link>
    </div>
  );

  // Staff pick their own home tiles (see data/staffTiles)
  const access = { can, isClubAdmin, staffDocumentsRequired: staffDocumentsOn(user), features: user?.features };
  const myTiles = homeTilesFor(user, access);
  const adminTiles = (
    <>
      <div className="dashboard-tiles dashboard-tiles--favourites">
        {myTiles.map(({ id, to, icon: Icon, label, className }) => (
          <Link key={id} to={to} className={`dashboard-tile ${className || ''}`}>
            <Icon className="dashboard-tile__icon" />
            <span className="dashboard-tile__label">{label}</span>
          </Link>
        ))}
      </div>
      <div className="dashboard-tiles__edit">
        <button type="button" className="bk-btn bk-btn--outline bk-btn--sm" onClick={() => setPickingTiles(true)}>
          Choose tiles
        </button>
      </div>
      {pickingTiles && (
        <HomeTilePicker
          tiles={tilesFor(access)}
          current={myTiles.map(t => t.id)}
          onSaved={saved => { updateUser({ ...user, homeTiles: saved.homeTiles }); setPickingTiles(false); }}
          onClose={() => setPickingTiles(false)}
        />
      )}
    </>
  );

  // ── Competition panels ────────────────────────────────────────────────────
  const COMP_STATUS = {
    INVITED:         { label: 'View invite', color: '#1565c0',                       action: true },
    ACCEPTED:        { label: 'Awaiting review', color: 'var(--text-muted, #888)',   action: false },
    PAYMENT_PENDING: { label: 'Pay now',     color: 'var(--warning-color, #e67e22)', action: true },
    PAID:            { label: 'Entered',     color: 'var(--success-color, #27ae60)', action: false },
    WAIVED:          { label: 'Entered',     color: 'var(--success-color, #27ae60)', action: false },
    DECLINED:        { label: 'Declined',    color: 'var(--text-muted, #888)',       action: false },
  };

  // Sections only appear when they have something in them. The noticeboard is
  // known straight away when cached; otherwise it appears once it has loaded.
  const hasNotices = noticeboardPosts.length > 0;

  const upcomingCompEntries = myCompEntries
    .filter(e => new Date(e.competitionEvent.startDate) >= new Date() && e.status !== 'DECLINED')
    .slice(0, 5);
  const compActionCount = upcomingCompEntries.filter(e => COMP_STATUS[e.status]?.action).length;

  const renderMemberComps = (collapsible) => (
    <DashboardPanel
      className="dashboard-comp"
      headerClassName="dashboard-comp__header"
      title={<span className="dashboard-comp__title">🏆 Competitions</span>}
      badge={compActionCount > 0 ? (
        <span className="dashboard-comp__badge">{compActionCount} need{compActionCount === 1 ? 's' : ''} attention</span>
      ) : null}
      collapsible={collapsible}
      defaultOpen={!collapsible}
    >
      {upcomingCompEntries.length === 0 ? (
        <p className="dashboard-comp__empty">No upcoming competitions.</p>
      ) : (
        <ul className="dashboard-comp__list">
          {upcomingCompEntries.map(entry => {
            const ev = entry.competitionEvent;
            const cfg = COMP_STATUS[entry.status] || { label: entry.status, color: 'inherit', action: false };
            return (
              <li key={entry.id} className="dashboard-comp__item">
                <div className="dashboard-comp__item-main">
                  <div className="dashboard-comp__item-name">{ev.name}</div>
                  <div className="dashboard-comp__item-meta">
                    {entry.gymnast.firstName} {entry.gymnast.lastName} &middot;{' '}
                    {new Date(ev.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                {cfg.action ? (
                  <Link
                    to={`/booking/competitions/${entry.id}/enter`}
                    className="dashboard-comp__action-btn"
                    style={{ background: cfg.color }}
                  >
                    {cfg.label}
                  </Link>
                ) : (
                  <span className="dashboard-comp__status" style={{ color: cfg.color }}>{cfg.label}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="dashboard-comp__footer">
        <Link to="/booking/competitions" className="dashboard-comp__cta">View all competitions</Link>
      </div>
    </DashboardPanel>
  );

  const pendingPaymentCount = compSummary?.paymentPending.length ?? 0;

  const renderStaffComps = (collapsible) => (
    <DashboardPanel
      className="dashboard-comp"
      headerClassName="dashboard-comp__header"
      title={<span className="dashboard-comp__title">🏆 Competitions</span>}
      badge={pendingPaymentCount > 0 ? (
        <span className="dashboard-comp__badge">{pendingPaymentCount} awaiting payment</span>
      ) : null}
      collapsible={collapsible}
      defaultOpen={!collapsible}
    >
      {upcomingEvents.length === 0 ? (
        <p className="dashboard-comp__empty">No upcoming competitions.</p>
      ) : (
        <ul className="dashboard-comp__list">
          {upcomingEvents.slice(0, 4).map(ev => {
            const pending = compSummary?.paymentPending.filter(e => e.competitionEvent.id === ev.id) ?? [];
            return (
              <Link key={ev.id} to={`/booking/admin/competitions/${ev.id}`} className="dashboard-comp__item">
                <div className="dashboard-comp__item-main">
                  <div className="dashboard-comp__item-name">{ev.name}</div>
                  <div className="dashboard-comp__item-meta">
                    {new Date(ev.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' · '}{ev._count.entries} entr{ev._count.entries === 1 ? 'y' : 'ies'}
                  </div>
                </div>
                {pending.length > 0 && (
                  <span className="dashboard-comp__action-btn" style={{ background: '#e67e22' }}>
                    {pending.length} awaiting
                  </span>
                )}
              </Link>
            );
          })}
        </ul>
      )}
      <div className="dashboard-comp__footer">
        <Link to="/booking/admin/competitions" className="dashboard-comp__cta">Manage competitions</Link>
      </div>
    </DashboardPanel>
  );

  // ── Today widget ───────────────────────────────────────────────────────────
  // Sessions on different apparatus that share an exact time slot are shown
  // as one row — they open the same combined register.
  const todaySlots = [];
  {
    const bySlot = new Map();
    for (const s of todaySessions) {
      const key = `${s.startTime}|${s.endTime}`;
      if (!bySlot.has(key)) bySlot.set(key, []);
      bySlot.get(key).push(s);
    }
    for (const group of bySlot.values()) {
      todaySlots.push(group.sort((a, b) => a.type.localeCompare(b.type)));
    }
    todaySlots.sort((a, b) => a[0].startTime.localeCompare(b[0].startTime));
  }

  const todayWidget = showTodayWidget && (
    <div className="dashboard-today">
      <div className="dashboard-today__title">Today</div>
      {todaySlots.length > 0 && (
        <div className="dashboard-today__slots">
          {todaySlots.map(group => {
            const first = group[0];
            const isActive = group.some(s => activeSessions.some(a => a.id === s.id));
            const apparatusLabel = [...new Set(group.map(typeName))].join(' + ');
            return (
              <button
                key={first.id}
                type="button"
                className={`dashboard-today__slot${isActive ? ' dashboard-today__slot--active' : ''}`}
                onClick={() => navigate('/booking/admin', { state: { preselect: first.id } })}
              >
                <span className="dashboard-today__slot-time">{first.startTime}–{first.endTime}</span>
                <span className="dashboard-today__slot-meta">{isActive ? 'Open register' : apparatusLabel}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="dashboard">
      <h1 className="dashboard__welcome">Welcome back, {user?.firstName}!</h1>
      <NotificationPrompt place="home" />
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      {isClubAdmin && <GymnastLimitBanner />}
      {/* The family's own memberships: a failed payment or money owed (staff with children too) */}
      {has('memberships') && <MembershipPaymentWarning />}
      <WelfareOutcomesCard />

      {isAdminOrCoach ? (
        <>
          <StaffDocumentsCard />
          {has('memberships') && can('payments') && <PaymentsBehindCard />}
          {hasNotices && renderNoticeboard(true)}
          {seesCompetitions && compsOn && upcomingEvents.length > 0 && renderStaffComps(true)}
          {todayLoading ? (
            <div className="dashboard-today dashboard-today--placeholder" aria-hidden="true">
              <div className="dashboard-today__title">Today</div>
              <div className="dashboard-today__skeleton" />
            </div>
          ) : todayWidget}
          <MySessionsCard />
          {adminTiles}
          {/* Only shows for staff with their own children on their account */}
          {has('gymnastApp') && <ChildSignInCard />}

          <div className="grid">
            {can('registers', 'MANAGE') && has('gymnastApp') && (
              <CodeOfDayCard />
            )}
          </div>

          {STAFF_UPDATES && (
            <DashboardPanel
              className="dashboard-birthdays dashboard-updates"
              headerClassName="dashboard-birthdays__header"
              title={<span className="dashboard-birthdays__title">{STAFF_UPDATES.title}</span>}
              collapsible
              defaultOpen={false}
            >
              {STAFF_UPDATES.sections.map(section => (
                <section key={section.heading} className="dashboard-updates__section">
                  <h3 className="dashboard-updates__heading">{section.heading}</h3>
                  <ul className="dashboard-updates__list">
                    {section.items.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </section>
              ))}
              <Link to="/booking/admin/changelog" className="dashboard-updates__more">See every update</Link>
            </DashboardPanel>
          )}
        </>
      ) : (
        <>
          {hasNotices && renderNoticeboard(false)}
          {compsOn && upcomingCompEntries.length > 0 && renderMemberComps(false)}
          {memberTiles}
          {has('gymnastApp') && <ChildSignInCard />}
        </>
      )}


    </div>
  );
};

export default Dashboard;
